import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, ArrowRight, Clock, Star } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AOT_STATUS_LABELS = {
    a_demander: 'A demander',
    hors_delais: 'Hors delais',
    en_cours: 'En cours',
    accepte: 'Accepte',
    refuse: 'Refuse',
};

const AOT_STATUS_COLORS = {
    a_demander: 'bg-amber-100 text-amber-700 border-amber-200',
    hors_delais: 'bg-red-100 text-red-700 border-red-200',
    en_cours: 'bg-blue-100 text-blue-700 border-blue-200',
    accepte: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    refuse: 'bg-red-100 text-red-700 border-red-200',
};

function formatHistoryDate(dateStr) {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '-';
    }
}

export function AotStatusHistoryDialog({ open, onOpenChange, aot }) {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && aot?.id) {
            setLoading(true);
            axios.get(`${API}/api/aot/${aot.id}/status-history`, { withCredentials: true })
                .then(({ data }) => setHistory(data || []))
                .catch(() => setHistory([]))
                .finally(() => setLoading(false));
        }
    }, [open, aot?.id]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="aot-status-history-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <Star size={20} className="text-[#2e7cd6] fill-[#2e7cd6]" />
                        Historique des statuts — {aot?.client_nom}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" />
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-center py-10 text-slate-400 text-sm">
                        Aucun changement de statut enregistré
                    </div>
                ) : (
                    <div className="relative pl-6 mt-2">
                        <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200" />
                        <div className="space-y-5">
                            {history.map((entry, idx) => (
                                <div key={idx} className="relative flex items-start gap-3" data-testid={`aot-history-entry-${idx}`}>
                                    <div className={`absolute -left-6 top-1 w-3 h-3 rounded-full border-2 ${idx === 0 ? 'bg-[#2e7cd6] border-[#2e7cd6]' : 'bg-white border-slate-300'}`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {entry.ancien_statut ? (
                                                <>
                                                    <Badge variant="outline" className={`${AOT_STATUS_COLORS[entry.ancien_statut] || 'bg-slate-100 text-slate-700'} border text-xs`}>
                                                        {AOT_STATUS_LABELS[entry.ancien_statut] || entry.ancien_statut}
                                                    </Badge>
                                                    <ArrowRight size={14} className="text-slate-400 shrink-0" />
                                                </>
                                            ) : null}
                                            <Badge variant="outline" className={`${AOT_STATUS_COLORS[entry.nouveau_statut] || 'bg-slate-100 text-slate-700'} border text-xs`}>
                                                {AOT_STATUS_LABELS[entry.nouveau_statut] || entry.nouveau_statut}
                                            </Badge>
                                            <span className="text-slate-300 mx-1">|</span>
                                            <Clock size={12} className="text-slate-400 shrink-0" />
                                            <span className="text-xs text-slate-400">{formatHistoryDate(entry.changed_at)}</span>
                                            <span className="text-slate-300">|</span>
                                            <span className="text-xs text-slate-400">{entry.changed_by || 'Système'}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
